import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { HttpContext } from '@angular/common/http';
import { ApiService } from '../api/api.service';
import { FormDataApiService } from '../apiFormData/FormDataApiService';
import { SKIP_SPINNER } from '../../interceptors/spinner.Interceptor';
import {
  BulkOperationState,
  ImpotsMessagesPreprocessSnapshotRow,
} from './bulk-operation.models';

const STORAGE_KEY = 'vesper_bulk_operation';

export interface StartBulkStatusPayload {
  taskIds: string[];
  hasTimeData: boolean;
  durationValue: number;
  plannedDateStr: string;
  timeType: string;
  action: string | null;
  description: string;
  statusIri: string;
  actionCode: string;
  label: string;
}

export interface StartBulkCommentPayload {
  taskIds: string[];
  commentContent: string;
  label: string;
}

export interface StartBulkImpotsMessagesPayload {
  operationId: string;
  label: string;
  /** Exécution navigateur : un POST par SIREN + fusion + save-report */
  sirens?: string[];
  preprocessRowsSnapshot?: ImpotsMessagesPreprocessSnapshotRow[];
  /** Alignement Mercure / état serveur uniquement (pas d’appels HTTP d’import côté client). */
  total?: number;
}

const BULK_HTTP_CONTEXT = new HttpContext().set(SKIP_SPINNER, true);

@Injectable({ providedIn: 'root' })
export class BulkOperationService {
  private _state$ = new BehaviorSubject<BulkOperationState | null>(null);
  readonly state$ = this._state$.asObservable();

  private aborted = false;

  constructor(
    private apiService: ApiService,
    private formDataApiService: FormDataApiService
  ) {
    this.resumeIfPending();
  }

  get snapshot(): BulkOperationState | null {
    return this._state$.value;
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  startBulkStatus(payload: StartBulkStatusPayload): void {
    const state: BulkOperationState = {
      id: this.generateId(),
      type: 'status',
      label: payload.label,
      hasTimeData: payload.hasTimeData,
      durationValue: payload.durationValue,
      plannedDateStr: payload.plannedDateStr,
      timeType: payload.timeType,
      action: payload.action,
      description: payload.description,
      statusIri: payload.statusIri,
      actionCode: payload.actionCode,
      commentContent: '',
      taskIds: payload.taskIds,
      processedCount: 0,
      operationStatus: 'running',
      errors: [],
      startedAt: new Date().toISOString(),
    };
    this.begin(state);
  }

  startBulkComment(payload: StartBulkCommentPayload): void {
    const state: BulkOperationState = {
      id: this.generateId(),
      type: 'comment',
      label: payload.label,
      hasTimeData: false,
      durationValue: 0,
      plannedDateStr: '',
      timeType: '',
      action: null,
      description: '',
      statusIri: '',
      actionCode: '',
      commentContent: payload.commentContent,
      taskIds: payload.taskIds,
      processedCount: 0,
      operationStatus: 'running',
      errors: [],
      startedAt: new Date().toISOString(),
    };
    this.begin(state);
  }

  startBulkImpotsMessages(payload: StartBulkImpotsMessagesPayload): void {
    const sirens =
      payload.sirens?.filter((s) => typeof s === 'string' && s.trim() !== '') ?? [];
    const snapshot = payload.preprocessRowsSnapshot;
    const hasSnapshot = Array.isArray(snapshot) && snapshot.length > 0;
    const runClientImport = sirens.length > 0 && hasSnapshot;

    if (sirens.length > 0 && !hasSnapshot) {
      const failedState: BulkOperationState = {
        id: payload.operationId,
        type: 'impots_messages',
        label: payload.label,
        hasTimeData: false,
        durationValue: 0,
        plannedDateStr: '',
        timeType: '',
        action: null,
        description: '',
        statusIri: '',
        actionCode: '',
        commentContent: '',
        taskIds: sirens,
        processedCount: 0,
        operationStatus: 'failed',
        errors: ['Données de pré-traitement manquantes pour l’import.'],
        startedAt: new Date().toISOString(),
      };
      this.begin(failedState, false);
      return;
    }

    const taskIds = runClientImport
      ? sirens
      : Array.from({ length: payload.total ?? 0 }, (_, index) => `siren_${index}`);

    const state: BulkOperationState = {
      id: payload.operationId,
      type: 'impots_messages',
      label: payload.label,
      hasTimeData: false,
      durationValue: 0,
      plannedDateStr: '',
      timeType: '',
      action: null,
      description: '',
      statusIri: '',
      actionCode: '',
      commentContent: '',
      taskIds,
      processedCount: 0,
      operationStatus: 'running',
      errors: [],
      startedAt: new Date().toISOString(),
      impotsMessagesPreprocessSnapshot: runClientImport ? snapshot : undefined,
    };

    this.begin(state, runClientImport);
  }

  updateBulkImpotsMessages(operationId: string, processedCount: number, errors: string[] = []): void {
    const current = this._state$.value;
    if (!current || current.id !== operationId || current.type !== 'impots_messages') {
      return;
    }

    this.patch({
      processedCount,
      errors,
      operationStatus: 'running',
    });
    this.persist(this._state$.value!);
  }

  completeBulkImpotsMessages(operationId: string, processedCount?: number, errors: string[] = []): void {
    const current = this._state$.value;
    if (!current || current.id !== operationId || current.type !== 'impots_messages') {
      return;
    }

    this.patch({
      processedCount: processedCount ?? current.taskIds.length,
      errors,
      operationStatus: 'completed',
    });
    this.persist(this._state$.value!);
  }

  failBulkImpotsMessages(operationId: string, errorMessage: string, processedCount?: number): void {
    const current = this._state$.value;
    if (!current || current.id !== operationId || current.type !== 'impots_messages') {
      return;
    }

    this.patch({
      processedCount: processedCount ?? current.processedCount,
      errors: [errorMessage],
      operationStatus: 'failed',
    });
    this.persist(this._state$.value!);
  }

  cancel(): void {
    const current = this._state$.value;
    if (!current || current.operationStatus !== 'running' || current.type === 'impots_messages') return;
    this.aborted = true;
    this.patch({ operationStatus: 'cancelled' });
    this.persist(this._state$.value!);
  }

  dismiss(): void {
    this.clearStorage();
    this._state$.next(null);
  }

  // ─── Internal execution ────────────────────────────────────────────────────

  private begin(state: BulkOperationState, executeImmediately: boolean = true): void {
    this.aborted = false;
    this._state$.next(state);
    this.persist(state);
    if (executeImmediately) {
      this.execute();
    }
  }

  private async execute(): Promise<void> {
    const state = this._state$.value;
    if (!state) return;

    if (state.type === 'status') {
      await this.executeStatus();
    } else if (state.type === 'comment') {
      await this.executeComment();
    } else if (state.type === 'impots_messages') {
      await this.executeImpotsMessages();
    }
  }

  private async executeStatus(): Promise<void> {
    while (true) {
      const state = this._state$.value;
      if (!state || this.aborted) return;

      const { taskIds, processedCount, hasTimeData, durationValue, plannedDateStr, timeType, action, description, statusIri } = state;

      if (processedCount >= taskIds.length) {
        this.patch({ operationStatus: 'completed' });
        this.persist(this._state$.value!);
        return;
      }

      const taskId = taskIds[processedCount];
      try {
        if (hasTimeData) {
          const startTime = new Date(plannedDateStr);
          const endTime = new Date(startTime.getTime() + durationValue * 60000);
          await lastValueFrom(
            this.apiService.create('task_times', {
              task: `/api/tasks/${taskId}`,
              startTime: startTime.toISOString(),
              endTime: endTime.toISOString(),
              timeType,
              duration: durationValue,
              description,
              action,
            }, null, BULK_HTTP_CONTEXT)
          );
        } else {
          await lastValueFrom(
            this.apiService.update('tasks', { id: taskId, status: statusIri }, null, BULK_HTTP_CONTEXT)
          );
        }
        this.patch({ processedCount: processedCount + 1 });
        this.persist(this._state$.value!);
      } catch (error: any) {
        const msg =
          error?.error?.['hydra:description'] ||
          error?.error?.message ||
          error?.message ||
          `Erreur tâche ID: ${taskId}`;
        this.patch({ operationStatus: 'failed', errors: [msg] });
        this.persist(this._state$.value!);
        return;
      }
    }
  }

  private async executeComment(): Promise<void> {
    while (true) {
      const state = this._state$.value;
      if (!state || this.aborted) return;

      const { taskIds, processedCount, commentContent } = state;

      if (processedCount >= taskIds.length) {
        this.patch({ operationStatus: 'completed' });
        this.persist(this._state$.value!);
        return;
      }

      const taskId = taskIds[processedCount];
      try {
        await lastValueFrom(
          this.formDataApiService.create('comments', {
            content: commentContent,
            ownerClass: 'TASK',
            ownerId: taskId,
            type: 'ACTIVITY',
          }, BULK_HTTP_CONTEXT)
        );
        this.patch({ processedCount: processedCount + 1 });
        this.persist(this._state$.value!);
      } catch (error: any) {
        const msg =
          error?.error?.['hydra:description'] ||
          error?.error?.message ||
          error?.message ||
          `Erreur tâche ID: ${taskId}`;
        this.patch({ operationStatus: 'failed', errors: [msg] });
        this.persist(this._state$.value!);
        return;
      }
    }
  }

  private async executeImpotsMessages(): Promise<void> {
    const initial = this._state$.value;
    if (!initial || initial.type !== 'impots_messages') return;

    const snapshot = initial.impotsMessagesPreprocessSnapshot;
    const sirens = initial.taskIds;

    if (!snapshot || snapshot.length === 0) {
      this.patch({
        operationStatus: 'failed',
        errors: ['Données de pré-traitement manquantes pour l’import.'],
      });
      this.persist(this._state$.value!);
      return;
    }

    const importBySiren = new Map<string, Record<string, unknown>>();

    for (let i = 0; i < sirens.length; i++) {
      const siren = sirens[i];
      if (!this._state$.value || this.aborted) return;

      try {
        const response = (await lastValueFrom(
          this.apiService.create(
            'import/impots-gouv/messages',
            { sirens: [siren], persistLog: false },
            null,
            BULK_HTTP_CONTEXT
          )
        )) as { report?: { results?: unknown[] } };

        const rows = response?.report?.results;
        if (Array.isArray(rows)) {
          for (const row of rows) {
            const r = row as Record<string, unknown>;
            const bid = String(r['businessId'] ?? '');
            if (bid !== '') {
              importBySiren.set(bid, r);
            }
          }
        }

        this.patch({ processedCount: i + 1 });
        this.persist(this._state$.value!);
      } catch (error: unknown) {
        const err = error as { error?: { error?: string; message?: string }; message?: string };
        const msg =
          err?.error?.error ||
          (err?.error as { 'hydra:description'?: string } | undefined)?.['hydra:description'] ||
          err?.error?.message ||
          err?.message ||
          `Erreur import SIREN ${siren}`;
        this.patch({
          operationStatus: 'failed',
          errors: [msg],
          processedCount: i,
        });
        this.persist(this._state$.value!);
        return;
      }
    }

    const merged = this.mergeImpotsMessagesReport(snapshot, importBySiren);

    try {
      await lastValueFrom(
        this.apiService.create(
          'import/impots-gouv/messages/save-report',
          { report: merged },
          null,
          BULK_HTTP_CONTEXT
        )
      );
      this.patch({
        operationStatus: 'completed',
        processedCount: sirens.length,
      });
      this.persist(this._state$.value!);
    } catch (error: unknown) {
      const err = error as { error?: { error?: string }; message?: string };
      const msg =
        err?.error?.error ||
        err?.message ||
        'Erreur lors de l’enregistrement du rapport.';
      this.patch({
        operationStatus: 'failed',
        errors: [msg],
      });
      this.persist(this._state$.value!);
    }
  }

  private mergeImpotsMessagesReport(
    preprocessRows: ImpotsMessagesPreprocessSnapshotRow[],
    importBySiren: Map<string, Record<string, unknown>>
  ): {
    filename: string;
    totalRows: number;
    validRows: number;
    invalidRows: number;
    results: Record<string, unknown>[];
  } {
    const results: Record<string, unknown>[] = [];
    let validRows = 0;
    let invalidRows = 0;

    for (const row of preprocessRows) {
      if (!row.isValid) {
        results.push({
          rowNumber: row.rowNumber,
          businessId: row.businessId,
          regNo: row.regNo,
          name: row.name,
          description: row.description,
          messages:
            row.messages.length > 0 ? row.messages : ['Dossier absent d’Impots.gouv.'],
          isValid: false,
        });
        invalidRows++;
        continue;
      }

      const imported = importBySiren.get(row.businessId);
      if (imported) {
        results.push(imported);
        if (imported['isValid'] === true) {
          validRows++;
        } else {
          invalidRows++;
        }
      } else {
        results.push({
          rowNumber: row.rowNumber,
          businessId: row.businessId,
          regNo: row.regNo,
          name: row.name,
          description: row.description,
          messages: ['Aucun résultat d’import pour ce SIREN.'],
          isValid: false,
        });
        invalidRows++;
      }
    }

    return {
      filename: 'impotsgouv_messages_import',
      totalRows: preprocessRows.length,
      validRows,
      invalidRows,
      results,
    };
  }

  // ─── Resume logic ──────────────────────────────────────────────────────────

  private resumeIfPending(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state: BulkOperationState = JSON.parse(raw);
      if (state.operationStatus === 'running') {
        if (state.type === 'impots_messages') {
          this.aborted = false;
          const failed: BulkOperationState = {
            ...state,
            operationStatus: 'failed',
            errors: [
              'L’import en masse des messages Impots.gouv ne peut pas reprendre après un rechargement de page. Relancez l’opération depuis Traitement de masse.',
            ],
          };
          this._state$.next(failed);
          this.persist(failed);
          return;
        }
        this.aborted = false;
        this._state$.next(state);
        setTimeout(() => this.execute(), 0);
      } else if (state.operationStatus !== 'completed' && state.operationStatus !== 'cancelled') {
        this._state$.next(state);
      }
    } catch {
      this.clearStorage();
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private patch(partial: Partial<BulkOperationState>): void {
    const current = this._state$.value;
    if (!current) return;
    this._state$.next({ ...current, ...partial });
  }

  private persist(state: BulkOperationState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage errors (private navigation, quota, etc.)
    }
  }

  private clearStorage(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  private generateId(): string {
    return `bulk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

}
